import { useState, useEffect, useRef, useCallback } from "react";
import { Layout } from "@/components/Layout";
import { Scan, Plus, Trash2, Upload, RefreshCw, Save, GitCompare, Check, X, History, FileUp, Radio, AlertTriangle, Info, Usb } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  ReferenceLine,
  ReferenceArea,
  LineChart,
  Line,
  Legend,
} from "recharts";
import { toast } from "sonner";
import { scansAPI, dashboardAPI, systemsAPI } from "@/lib/api";
import { IntermodCalculator } from "@/components/IntermodCalculator";
import { SpectrumZoom } from "@/components/SpectrumZoom";

// Helper to parse CSV with quoted fields
const parseCSVLine = (line) => {
  const result = [];
  let current = '';
  let inQuotes = false;
  
  for (let i = 0; i < line.length; i++) {
    const char = line[i];
    
    if (char === '"') {
      inQuotes = !inQuotes;
    } else if (char === ',' && !inQuotes) {
      result.push(current.trim());
      current = '';
    } else {
      current += char;
    }
  }
  result.push(current.trim());
  return result;
};

// Relacart DISCOVER CSV Parser
const parseRelacartCSV = (content, fileName) => {
  const lines = content.trim().split('\n');
  if (lines.length < 2) return null;
  
  const firstLine = lines[0];
  const firstDataLine = lines[1] || '';
  
  // Format 1: Spectrum data (freq_khz, level_dbm) - no headers
  // Example: 240725,-85
  const isNumericFormat = /^\d+[,;\s]+-?\d+(\.\d+)?$/.test(firstLine.trim());
  
  if (isNumericFormat) {
    // Pure spectrum data - frequencies in kHz
    const scanData = [];
    let minFreq = Infinity, maxFreq = -Infinity;
    
    lines.forEach(line => {
      const trimmed = line.trim();
      if (!trimmed) return;
      
      // Support comma, semicolon or space as separator
      const parts = trimmed.split(/[,;\s]+/);
      if (parts.length >= 2) {
        let freq = parseFloat(parts[0]);
        const level = parseFloat(parts[1]);
        
        // Convert kHz to MHz if necessary (values > 1000 are likely kHz)
        if (freq > 1000) {
          freq = freq / 1000;
        }
        
        if (!isNaN(freq) && !isNaN(level)) {
          scanData.push({ frequency: parseFloat(freq.toFixed(3)), level: level });
          minFreq = Math.min(minFreq, freq);
          maxFreq = Math.max(maxFreq, freq);
        }
      }
    });
    
    if (scanData.length > 0) {
      // Sort by frequency
      scanData.sort((a, b) => a.frequency - b.frequency);
      
      return {
        type: 'spectrum',
        name: fileName.replace('.csv', ''),
        scan_data: scanData,
        band_min: Math.floor(minFreq),
        band_max: Math.ceil(maxFreq),
        total_points: scanData.length,
        detected_peaks: detectPeaks(scanData)
      };
    }
  }
  
  // Format 2: Equipment/coordination data with headers
  // freq,freqName,siteName,zoneName,groupName,tvChannel,modelId,manufacturer,model,range,color,bandwidth,...
  if (firstLine.toLowerCase().includes('freq') && firstLine.includes(',')) {
    const headers = parseCSVLine(firstLine).map(h => h.toLowerCase());
    const freqIdx = headers.indexOf('freq');
    const nameIdx = headers.indexOf('freqname');
    const manufacturerIdx = headers.indexOf('manufacturer');
    const modelIdx = headers.indexOf('model');
    const groupIdx = headers.indexOf('groupname');
    const bandwidthIdx = headers.indexOf('bandwidth');
    const siteIdx = headers.indexOf('sitename');
    
    if (freqIdx === -1) return null;
    
    const systems = [];
    const frequencies = [];
    let siteName = '';
    
    for (let i = 1; i < lines.length; i++) {
      const parts = parseCSVLine(lines[i]);
      if (parts.length <= freqIdx) continue;
      
      const freq = parseFloat(parts[freqIdx]);
      if (isNaN(freq)) continue;
      
      frequencies.push(freq);
      
      if (siteIdx >= 0 && parts[siteIdx]) {
        siteName = parts[siteIdx];
      }
      
      systems.push({
        frequency: freq,
        name: parts[nameIdx] || `CH ${i}`,
        manufacturer: parts[manufacturerIdx] || 'Unknown',
        model: parts[modelIdx] || 'Unknown',
        group: parts[groupIdx] || 'Default',
        bandwidth: bandwidthIdx >= 0 ? parseInt(parts[bandwidthIdx]) / 1000 : 200 // Convert Hz to kHz
      });
    }
    
    if (systems.length > 0) {
      const minFreq = Math.min(...frequencies);
      const maxFreq = Math.max(...frequencies);
      
      // Create spectrum-like data from the systems (as markers)
      const scanData = systems.map(s => ({
        frequency: s.frequency,
        level: -40 // Active transmission level
      }));
      
      return {
        type: 'coordination',
        name: fileName.replace('.csv', ''),
        siteName: siteName,
        systems: systems,
        scan_data: scanData,
        band_min: Math.floor(minFreq) - 10,
        band_max: Math.ceil(maxFreq) + 10,
        total_systems: systems.length,
        groups: [...new Set(systems.map(s => s.group))]
      };
    }
  }
  
  return null;
};

// Detect peaks in spectrum data (interference)
const detectPeaks = (scanData, threshold = -70) => {
  const peaks = [];
  
  for (let i = 1; i < scanData.length - 1; i++) {
    const prev = scanData[i - 1].level;
    const curr = scanData[i].level;
    const next = scanData[i + 1].level;
    
    // Peak if higher than neighbors and above threshold
    if (curr > prev && curr > next && curr > threshold) {
      peaks.push({
        frequency: scanData[i].frequency,
        level: curr,
        severity: curr > -50 ? 'high' : curr > -60 ? 'medium' : 'low'
      });
    }
  }
  
  return peaks;
};

const CustomTooltip = ({ active, payload, label }) => {
  if (active && payload && payload.length) {
    return (
      <div className="bg-[#1A1A1A] border border-white/10 rounded-sm p-3">
        <p className="font-mono text-sm text-[#34C759]">{label} MHz</p>
        <p className="font-mono text-xs text-[#A1A1AA]">
          Level: {payload[0].value} dBm
        </p>
      </div>
    );
  }
  return null;
};

export default function ScanPage({ user, onLogout }) {
  const [scans, setScans] = useState([]);
  const [systems, setSystems] = useState([]);
  const [liveSpectrum, setLiveSpectrum] = useState([]);
  const [selectedScan, setSelectedScan] = useState(null);
  const [showDialog, setShowDialog] = useState(false);
  const [showSaveDialog, setShowSaveDialog] = useState(false);
  const [showImportDialog, setShowImportDialog] = useState(false);
  const [loading, setLoading] = useState(true);
  const [zoomRange, setZoomRange] = useState({ min: 480, max: 980 });
  const [compareMode, setCompareMode] = useState(false);
  const [compareScans, setCompareScans] = useState([]);
  const [savingScan, setSavingScan] = useState(false);
  const [quickSaveName, setQuickSaveName] = useState("");
  const [importedData, setImportedData] = useState(null);
  const [importLoading, setImportLoading] = useState(false);
  const [usbConnected, setUsbConnected] = useState(false);
  const [usbScanData, setUsbScanData] = useState([]);
  const [noiseFloorLevel, setNoiseFloorLevel] = useState(-85);
  const fileInputRef = useRef(null);
  
  // Priority zones for frequency filtering (6 MHz bands)
  const [priorityZones, setPriorityZones] = useState(() => {
    const saved = localStorage.getItem('avx_priority_zones');
    return saved ? JSON.parse(saved) : [];
  });
  const [showZoneDialog, setShowZoneDialog] = useState(false);
  const [newZone, setNewZone] = useState({ startFreq: 500, priority: 'block' });
  const ZONE_BANDWIDTH = 6; // MHz
  
  // Priority zone colors and labels
  const priorityConfig = {
    high: { color: '#34C759', label: 'Alta', opacity: 0.4 },
    medium: { color: '#FFCC00', label: 'Media', opacity: 0.35 },
    low: { color: '#8E8E93', label: 'Baja', opacity: 0.25 },
    block: { color: '#FF3B30', label: 'Bloqueado', opacity: 0.5 }
  };
  
  // Save priority zones to localStorage
  useEffect(() => {
    localStorage.setItem('avx_priority_zones', JSON.stringify(priorityZones));
  }, [priorityZones]);
  
  // Add a new priority zone
  const addPriorityZone = () => {
    const zone = {
      id: Date.now().toString(),
      startFreq: parseFloat(newZone.startFreq),
      endFreq: parseFloat(newZone.startFreq) + ZONE_BANDWIDTH,
      priority: newZone.priority
    };
    setPriorityZones(prev => [...prev, zone]);
    setShowZoneDialog(false);
    setNewZone({ startFreq: 500, priority: 'block' });
  };
  
  // Remove a priority zone
  const removePriorityZone = (zoneId) => {
    setPriorityZones(prev => prev.filter(z => z.id !== zoneId));
  };
  
  const [form, setForm] = useState({
    name: "",
    band_min: 480,
    band_max: 980,
    scan_data_text: "",
  });

  // Colors for comparison mode
  const compareColors = ["#34C759", "#8B5CF6", "#FF9500", "#FF2D55", "#5AC8FA"];

  // Handle USB scan data from Relacart DISCOVER
  const handleUsbScanData = useCallback((data) => {
    if (data.type === 'point') {
      setUsbScanData(prev => {
        // Keep only last 500 points for performance
        const newData = [...prev, { frequency: data.frequency, level: data.level }];
        if (newData.length > 500) {
          return newData.slice(-500);
        }
        return newData;
      });
      
      // Also update live spectrum if USB is connected
      setLiveSpectrum(prev => {
        const existingIndex = prev.findIndex(p => Math.abs(p.frequency - data.frequency) < 0.5);
        if (existingIndex >= 0) {
          const updated = [...prev];
          updated[existingIndex] = { frequency: data.frequency, level: data.level };
          return updated;
        }
        const newSpectrum = [...prev, { frequency: data.frequency, level: data.level }];
        return newSpectrum.sort((a, b) => a.frequency - b.frequency);
      });
    } else if (data.type === 'scan_start') {
      setUsbScanData([]);
      toast.info("Iniciando escaneo USB...");
    } else if (data.type === 'scan_end') {
      toast.success(`Escaneo USB completado`);
    }
  }, []);

  const fetchData = async () => {
    try {
      const [scansRes, spectrumRes, systemsRes] = await Promise.all([
        scansAPI.getAll(),
        dashboardAPI.getSpectrum(),
        systemsAPI.getAll(),
      ]);
      setScans(scansRes.data);
      setSystems(systemsRes.data || []);
      setLiveSpectrum(spectrumRes.data.spectrum);
    } catch (error) {
      // Silently handle scan fetch error
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const refreshSpectrum = async () => {
    try {
      const res = await dashboardAPI.getSpectrum();
      setLiveSpectrum(res.data.spectrum);
      toast.success("Espectro actualizado");
    } catch (error) {
      toast.error("Error al actualizar espectro");
    }
  };

  // Quick save current spectrum
  const handleQuickSave = async () => {
    if (!quickSaveName.trim()) {
      toast.error("Ingresa un nombre para el escaneo");
      return;
    }
    
    setSavingScan(true);
    try {
      await scansAPI.create({
        name: quickSaveName,
        band_min: zoomRange.min,
        band_max: zoomRange.max,
        scan_data: liveSpectrum,
      });
      toast.success(`Escaneo "${quickSaveName}" guardado`);
      setShowSaveDialog(false);
      setQuickSaveName("");
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al guardar escaneo");
    } finally {
      setSavingScan(false);
    }
  };

  // Toggle scan for comparison
  const toggleCompare = (scan) => {
    if (compareScans.find(s => s.id === scan.id)) {
      setCompareScans(compareScans.filter(s => s.id !== scan.id));
    } else if (compareScans.length < 5) {
      setCompareScans([...compareScans, scan]);
    } else {
      toast.error("Máximo 5 escaneos para comparar");
    }
  };

  // Prepare data for comparison chart
  const prepareCompareData = () => {
    if (compareScans.length === 0) return [];
    
    // Get all unique frequencies from all scans
    const allFrequencies = new Set();
    compareScans.forEach(scan => {
      scan.scan_data?.forEach(point => {
        if (point.frequency >= zoomRange.min && point.frequency <= zoomRange.max) {
          allFrequencies.add(point.frequency);
        }
      });
    });
    
    // Create combined data
    const sortedFreqs = Array.from(allFrequencies).sort((a, b) => a - b);
    return sortedFreqs.map(freq => {
      const dataPoint = { frequency: freq };
      compareScans.forEach((scan, idx) => {
        const match = scan.scan_data?.find(p => Math.abs(p.frequency - freq) < 0.5);
        dataPoint[`scan_${idx}`] = match?.level || null;
      });
      return dataPoint;
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      // Parse scan data from text (format: frequency,level per line)
      const lines = form.scan_data_text.trim().split("\n");
      const scan_data = lines.map((line) => {
        const [freq, level] = line.split(",").map((v) => parseFloat(v.trim()));
        return { frequency: freq, level: level };
      });

      await scansAPI.create({
        name: form.name,
        band_min: form.band_min,
        band_max: form.band_max,
        scan_data,
      });
      toast.success("Escaneo guardado");
      setShowDialog(false);
      setForm({
        name: "",
        band_min: 480,
        band_max: 980,
        scan_data_text: "",
      });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al guardar escaneo");
    }
  };

  const handleDelete = async (scanId) => {
    try {
      await scansAPI.delete(scanId);
      toast.success("Escaneo eliminado");
      if (selectedScan?.id === scanId) {
        setSelectedScan(null);
      }
      fetchData();
    } catch (error) {
      toast.error("Error al eliminar escaneo");
    }
  };

  const generateSampleData = () => {
    const sampleData = [];
    for (let freq = 480; freq <= 980; freq += 4) {
      const level = -90 + Math.random() * 20;
      sampleData.push(`${freq},${level.toFixed(1)}`);
    }
    setForm({ ...form, scan_data_text: sampleData.join("\n") });
  };

  // Filter spectrum data based on zoom range
  const displaySpectrum = (selectedScan?.scan_data || liveSpectrum).filter(
    (d) => d.frequency >= zoomRange.min && d.frequency <= zoomRange.max
  );

  // Get active frequencies from systems for intermod calculation
  const activeFrequencies = systems
    .filter((s) => s.frequency && s.frequency > 0)
    .map((s) => parseFloat(s.frequency));

  const handleZoomChange = (min, max) => {
    setZoomRange({ min, max });
  };

  // Handle Relacart CSV file import
  const handleFileImport = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    if (!file.name.endsWith('.csv')) {
      toast.error('Por favor selecciona un archivo CSV');
      return;
    }
    
    setImportLoading(true);
    try {
      const content = await file.text();
      const parsed = parseRelacartCSV(content, file.name);
      
      if (!parsed) {
        toast.error('No se pudo reconocer el formato del archivo');
        return;
      }
      
      setImportedData(parsed);
      setShowImportDialog(true);
      
      // Auto-adjust zoom to show imported data
      if (parsed.band_min && parsed.band_max) {
        setZoomRange({ min: parsed.band_min, max: parsed.band_max });
      }
      
      if (parsed.type === 'spectrum') {
        toast.success(`Escaneo detectado: ${parsed.total_points} puntos, ${parsed.detected_peaks?.length || 0} picos de interferencia`);
      } else {
        toast.success(`Coordinación detectada: ${parsed.total_systems} sistemas en ${parsed.groups?.length || 1} grupos`);
      }
    } catch (error) {
      // Error handled silently
      toast.error('Error al leer el archivo');
    } finally {
      setImportLoading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  // Save imported scan to history
  const handleSaveImportedScan = async () => {
    if (!importedData) return;
    
    setSavingScan(true);
    try {
      await scansAPI.create({
        name: importedData.name,
        band_min: importedData.band_min,
        band_max: importedData.band_max,
        scan_data: importedData.scan_data,
      });
      
      toast.success(`Escaneo "${importedData.name}" guardado en historial`);
      setShowImportDialog(false);
      setImportedData(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al guardar escaneo');
    } finally {
      setSavingScan(false);
    }
  };

  // View imported scan without saving
  const handleViewImportedScan = () => {
    if (!importedData) return;
    
    // Create a temporary scan object for viewing
    const tempScan = {
      id: 'imported-temp',
      name: `${importedData.name} (Sin guardar)`,
      scan_data: importedData.scan_data,
      band_min: importedData.band_min,
      band_max: importedData.band_max,
      created_at: new Date().toISOString()
    };
    
    setSelectedScan(tempScan);
    setShowImportDialog(false);
    toast.info('Mostrando escaneo importado (no guardado)');
  };

  return (
    <Layout user={user} onLogout={onLogout}>
      <div className="top-bar">
        <div>
          <h1 className="font-heading text-xl font-bold text-[#F4F4F5]">
            Escaneo RF
          </h1>
          <p className="text-sm text-[#71717A]">
            Visualización, historial y comparación del espectro RF
          </p>
        </div>
        <div className="flex gap-2">
          <Button
            onClick={refreshSpectrum}
            className="btn-tactical btn-tactical-secondary"
            data-testid="refresh-spectrum-button"
          >
            <RefreshCw className="w-4 h-4 mr-2" />
            Actualizar
          </Button>
          <Button
            onClick={() => setShowSaveDialog(true)}
            className="btn-tactical btn-tactical-primary"
            data-testid="save-scan-button"
          >
            <Save className="w-4 h-4 mr-2" />
            Guardar Escaneo
          </Button>
          <Button
            onClick={() => {
              setCompareMode(!compareMode);
              if (compareMode) {
                setCompareScans([]);
              }
            }}
            className={`btn-tactical ${compareMode ? 'bg-[#8B5CF6] hover:bg-[#8B5CF6]/90' : 'btn-tactical-secondary'}`}
            data-testid="compare-mode-button"
          >
            <GitCompare className="w-4 h-4 mr-2" />
            {compareMode ? 'Salir Comparar' : 'Comparar'}
          </Button>
          
          {/* Hidden file input for Relacart import */}
          <input
            type="file"
            ref={fileInputRef}
            accept=".csv"
            onChange={handleFileImport}
            className="hidden"
            data-testid="relacart-file-input"
          />
          
          <Button
            onClick={() => fileInputRef.current?.click()}
            disabled={importLoading}
            className="btn-tactical btn-tactical-secondary"
            data-testid="import-relacart-button"
          >
            {importLoading ? (
              <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <FileUp className="w-4 h-4 mr-2" />
            )}
            Importar CSV
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        {/* Spectrum Display */}
        <div className="lg:col-span-3">
          <div className="spectrum-container" data-testid="scan-spectrum-chart">
            <div className="spectrum-header">
              <div>
                <h2 className="font-heading font-semibold text-[#F4F4F5]">
                  {compareMode && compareScans.length > 0 
                    ? `Comparando ${compareScans.length} escaneos` 
                    : selectedScan 
                      ? selectedScan.name 
                      : "Espectro en Vivo"}
                </h2>
                <p className="text-xs text-[#71717A]">
                  {compareMode && compareScans.length > 0
                    ? "Modo comparación activo"
                    : selectedScan
                      ? `Guardado: ${new Date(selectedScan.created_at).toLocaleString()}`
                      : "Actualización automática"}
                </p>
              </div>
              <div className="flex items-center gap-4">
                <SpectrumZoom
                  minFreq={480}
                  maxFreq={980}
                  currentMin={zoomRange.min}
                  currentMax={zoomRange.max}
                  onRangeChange={handleZoomChange}
                  compact={true}
                />
                {selectedScan && (
                  <Button
                    onClick={() => setSelectedScan(null)}
                    variant="ghost"
                    className="text-xs text-[#71717A] hover:text-[#F4F4F5]"
                  >
                    Ver en vivo
                  </Button>
                )}
              </div>
            </div>

            <div className="h-80 relative">
              {/* Priority Zones Visual Overlay */}
              {!compareMode && priorityZones.length > 0 && (
                <div className="absolute inset-0 pointer-events-none z-10" style={{ 
                  left: '40px', // Account for Y axis
                  right: '10px',
                  top: '10px',
                  bottom: '25px' // Account for X axis
                }}>
                  {priorityZones.map((zone) => {
                    const chartWidth = 100; // percentage
                    const rangeSpan = zoomRange.max - zoomRange.min;
                    
                    // Calculate position as percentage
                    const leftPercent = ((zone.startFreq - zoomRange.min) / rangeSpan) * chartWidth;
                    const widthPercent = ((zone.endFreq - zone.startFreq) / rangeSpan) * chartWidth;
                    
                    // Skip if zone is outside visible range
                    if (leftPercent > 100 || leftPercent + widthPercent < 0) return null;
                    
                    const config = priorityConfig[zone.priority];
                    const isBlocked = zone.priority === 'block';
                    
                    return (
                      <div
                        key={`overlay-${zone.id}`}
                        className="absolute h-full"
                        style={{
                          left: `${Math.max(0, leftPercent)}%`,
                          width: `${Math.min(widthPercent, 100 - Math.max(0, leftPercent))}%`,
                          backgroundColor: config.color,
                          opacity: isBlocked ? 0.4 : 0.25,
                          borderLeft: `2px solid ${config.color}`,
                          borderRight: `2px solid ${config.color}`,
                          borderStyle: isBlocked ? 'dashed' : 'solid'
                        }}
                      />
                    );
                  })}
                </div>
              )}
              
              {compareMode && compareScans.length > 0 ? (
                /* Comparison Chart */
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart
                    data={prepareCompareData()}
                    margin={{ top: 10, right: 10, left: -20, bottom: 0 }}
                  >
                    <CartesianGrid
                      strokeDasharray="3 3"
                      stroke="rgba(255,255,255,0.05)"
                      vertical={false}
                    />
                    <XAxis
                      dataKey="frequency"
                      tick={{
                        fill: "#71717A",
                        fontSize: 10,
                        fontFamily: "JetBrains Mono",
                      }}
                      tickLine={{ stroke: "rgba(255,255,255,0.1)" }}
                      axisLine={{ stroke: "rgba(255,255,255,0.1)" }}
                      tickFormatter={(v) => `${v}`}
                      domain={[zoomRange.min, zoomRange.max]}
                    />
                    <YAxis
                      tick={{
                        fill: "#71717A",
                        fontSize: 10,
                        fontFamily: "JetBrains Mono",
                      }}
                      tickLine={{ stroke: "rgba(255,255,255,0.1)" }}
                      axisLine={{ stroke: "rgba(255,255,255,0.1)" }}
                      domain={[-100, -20]}
                      tickFormatter={(v) => `${v}`}
                    />
                    <Tooltip 
                      content={({ active, payload, label }) => {
                        if (active && payload && payload.length) {
                          return (
                            <div className="bg-[#1A1A1A] border border-white/10 rounded-sm p-3">
                              <p className="font-mono text-sm text-white mb-2">{label} MHz</p>
                              {payload.map((entry, idx) => (
                                <p key={idx} className="font-mono text-xs" style={{ color: entry.color }}>
                                  {compareScans[idx]?.name}: {entry.value?.toFixed(1)} dBm
                                </p>
                              ))}
                            </div>
                          );
                        }
                        return null;
                      }}
                    />
                    <Legend 
                      wrapperStyle={{ paddingTop: 10 }}
                      formatter={(value, entry, index) => (
                        <span style={{ color: compareColors[index] }} className="text-xs">
                          {compareScans[index]?.name}
                        </span>
                      )}
                    />
                    <ReferenceLine
                      y={noiseFloorLevel}
                      stroke="#FF3B30"
                      strokeDasharray="5 5"
                      strokeOpacity={0.5}
                    />
                    {compareScans.map((scan, idx) => (
                      <Line
                        key={scan.id}
                        type="monotone"
                        dataKey={`scan_${idx}`}
                        stroke={compareColors[idx]}
                        strokeWidth={2}
                        dot={false}
                        name={scan.name}
                        connectNulls
                      />
                    ))}
                  </LineChart>
                </ResponsiveContainer>
              ) : (
                /* Normal Spectrum Chart */
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart
                    data={displaySpectrum}
                    margin={{ top: 10, right: 10, left: -20, bottom: 0 }}
                  >
                    <defs>
                      <linearGradient id="scanGradient" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#34C759" stopOpacity={0.3} />
                        <stop offset="95%" stopColor="#34C759" stopOpacity={0} />
                      </linearGradient>
                      {/* Priority zone patterns */}
                      <pattern id="blockedPattern" patternUnits="userSpaceOnUse" width="8" height="8">
                        <path d="M-1,1 l2,-2 M0,8 l8,-8 M7,9 l2,-2" stroke="#FF3B30" strokeWidth="2" strokeOpacity="0.6"/>
                      </pattern>
                    </defs>
                    <CartesianGrid
                      strokeDasharray="3 3"
                      stroke="rgba(255,255,255,0.05)"
                      vertical={false}
                    />
                    <XAxis
                      dataKey="frequency"
                      tick={{
                        fill: "#71717A",
                        fontSize: 10,
                        fontFamily: "JetBrains Mono",
                      }}
                      tickLine={{ stroke: "rgba(255,255,255,0.1)" }}
                      axisLine={{ stroke: "rgba(255,255,255,0.1)" }}
                      tickFormatter={(v) => `${v}`}
                      interval={Math.max(1, Math.floor(displaySpectrum.length / 12))}
                      domain={[zoomRange.min, zoomRange.max]}
                    />
                    <YAxis
                      tick={{
                        fill: "#71717A",
                        fontSize: 10,
                        fontFamily: "JetBrains Mono",
                      }}
                      tickLine={{ stroke: "rgba(255,255,255,0.1)" }}
                      axisLine={{ stroke: "rgba(255,255,255,0.1)" }}
                      domain={[-100, -20]}
                      tickFormatter={(v) => `${v}`}
                    />
                    <Tooltip content={<CustomTooltip />} />
                    <ReferenceLine
                      y={noiseFloorLevel}
                      stroke="#FF3B30"
                      strokeDasharray="5 5"
                      strokeOpacity={0.5}
                      label={{
                        value: "Noise Floor",
                        fill: "#FF3B30",
                        fontSize: 10,
                        position: "right",
                      }}
                    />
                    <ReferenceLine
                      y={-50}
                      stroke="#FFCC00"
                      strokeDasharray="5 5"
                      strokeOpacity={0.3}
                    />
                    {/* LTE Band Warning Zone (698-806 MHz) - only show if in zoom range */}
                    {zoomRange.min < 806 && zoomRange.max > 698 && (
                      <>
                        <ReferenceLine
                          x={698}
                          stroke="#FF3B30"
                          strokeOpacity={0.3}
                          label={{
                            value: "LTE",
                            fill: "#FF3B30",
                            fontSize: 9,
                            position: "top",
                          }}
                        />
                        <ReferenceLine
                          x={806}
                          stroke="#FF3B30"
                          strokeOpacity={0.3}
                        />
                      </>
                    )}
                    
                    {/* System frequency markers */}
                    {activeFrequencies.map((freq, idx) => (
                      freq >= zoomRange.min && freq <= zoomRange.max && (
                        <ReferenceLine
                          key={`sys-${idx}`}
                          x={freq}
                          stroke="#34C759"
                          strokeOpacity={0.8}
                          strokeWidth={2}
                        />
                      )
                    ))}
                    
                    <Area
                      type="monotone"
                      dataKey="level"
                      stroke="#34C759"
                      fill="url(#scanGradient)"
                      strokeWidth={1.5}
                    />
                    
                  </AreaChart>
                </ResponsiveContainer>
              )}
            </div>

            <div className="flex items-center justify-between mt-4 pt-4 border-t border-white/[0.08]">
              <div className="flex items-center gap-4 text-xs flex-wrap">
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 rounded-sm bg-[#34C759]" />
                  <span className="text-[#71717A]">Señal</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-6 h-0.5 bg-[#FF3B30]" style={{ borderStyle: "dashed" }} />
                  <span className="text-[#71717A]">Piso de Ruido ({noiseFloorLevel} dBm)</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-6 h-0.5 bg-[#FFCC00]" style={{ borderStyle: "dashed" }} />
                  <span className="text-[#71717A]">Umbral Fuerte (-50 dBm)</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-4 h-3 bg-[#FF3B30]/20 border border-[#FF3B30]/50" />
                  <span className="text-[#71717A]">Zona LTE (698-806 MHz)</span>
                </div>
              </div>
              
              {/* Noise Floor Control */}
              <div className="flex items-center gap-3">
                <span className="text-xs text-[#71717A]">Piso de Ruido:</span>
                <input
                  type="range"
                  min="-120"
                  max="-50"
                  value={noiseFloorLevel}
                  onChange={(e) => setNoiseFloorLevel(parseInt(e.target.value))}
                  className="w-24 h-1.5 bg-[#2A2A2A] rounded-lg appearance-none cursor-pointer accent-[#FF3B30]"
                  data-testid="noise-floor-slider-scan"
                />
                <span className="text-xs text-[#FF3B30] font-mono w-14">{noiseFloorLevel} dBm</span>
              </div>
            </div>
            
            {/* Priority Zones Panel */}
            <div className="mt-4 pt-4 border-t border-white/[0.08]">
              <div className="flex items-center justify-between mb-3">
                <h4 className="text-sm font-medium text-[#F4F4F5] flex items-center gap-2">
                  <Radio className="w-4 h-4 text-[#8B5CF6]" />
                  Zonas de Prioridad (6 MHz)
                </h4>
                <Button
                  size="sm"
                  onClick={() => setShowZoneDialog(true)}
                  className="h-7 px-2 bg-[#8B5CF6] hover:bg-[#8B5CF6]/80 text-white text-xs"
                  data-testid="add-priority-zone-button"
                >
                  <Plus className="w-3 h-3 mr-1" />
                  Agregar Zona
                </Button>
              </div>
              
              {priorityZones.length === 0 ? (
                <p className="text-xs text-[#71717A] italic">
                  No hay zonas de prioridad definidas. Agrega zonas para filtrar rangos de frecuencia.
                </p>
              ) : (
                <div className="flex flex-wrap gap-2">
                  {priorityZones.map((zone) => (
                    <div 
                      key={zone.id}
                      className="flex items-center gap-2 px-2 py-1 rounded-sm text-xs"
                      style={{ 
                        backgroundColor: `${priorityConfig[zone.priority].color}20`,
                        border: `1px solid ${priorityConfig[zone.priority].color}50`
                      }}
                    >
                      <div 
                        className="w-2 h-2 rounded-full"
                        style={{ backgroundColor: priorityConfig[zone.priority].color }}
                      />
                      <span className="text-[#F4F4F5] font-mono">
                        {zone.startFreq}-{zone.endFreq} MHz
                      </span>
                      <span 
                        className="text-xs px-1 rounded"
                        style={{ 
                          backgroundColor: `${priorityConfig[zone.priority].color}30`,
                          color: priorityConfig[zone.priority].color
                        }}
                      >
                        {priorityConfig[zone.priority].label}
                      </span>
                      <button
                        onClick={() => removePriorityZone(zone.id)}
                        className="text-[#71717A] hover:text-[#FF3B30] ml-1"
                      >
                        <X className="w-3 h-3" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
              
              {/* Priority zones legend */}
              {priorityZones.length > 0 && (
                <div className="flex items-center gap-4 mt-3 pt-2 border-t border-white/[0.04]">
                  {Object.entries(priorityConfig).map(([key, config]) => (
                    <div key={key} className="flex items-center gap-1 text-xs">
                      <div 
                        className="w-3 h-2 rounded-sm"
                        style={{ backgroundColor: config.color, opacity: config.opacity + 0.3 }}
                      />
                      <span className="text-[#71717A]">{config.label}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Intermodulation Calculator */}
          {activeFrequencies.length >= 2 && (
            <div className="mt-6">
              <IntermodCalculator 
                frequencies={activeFrequencies}
                minFreq={zoomRange.min}
                maxFreq={zoomRange.max}
              />
            </div>
          )}
        </div>

        {/* Saved Scans List */}
        <div className="lg:col-span-1">
          <div className="bg-[#111111] border border-white/[0.08] rounded-sm p-4">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-heading font-semibold text-[#F4F4F5] flex items-center gap-2">
                <History className="w-4 h-4 text-[#34C759]" />
                Historial
              </h3>
              {compareMode && compareScans.length > 0 && (
                <span className="text-xs px-2 py-1 rounded bg-[#8B5CF6]/20 text-[#8B5CF6]">
                  {compareScans.length} seleccionados
                </span>
              )}
            </div>
            
            {compareMode && (
              <div className="mb-3 p-2 bg-[#8B5CF6]/10 border border-[#8B5CF6]/30 rounded-sm">
                <p className="text-xs text-[#8B5CF6]">
                  Selecciona hasta 5 escaneos para comparar
                </p>
              </div>
            )}
            
            {loading ? (
              <p className="text-[#71717A] text-sm">Cargando...</p>
            ) : scans.length === 0 ? (
              <div className="text-center py-8">
                <Scan className="w-10 h-10 mx-auto mb-3 text-[#71717A] opacity-30" />
                <p className="text-[#71717A] text-sm">Sin escaneos</p>
                <p className="text-[#52525B] text-xs mt-1">Guarda el espectro actual para crear historial</p>
              </div>
            ) : (
              <div className="space-y-2 max-h-96 overflow-y-auto pr-1" data-testid="scans-list">
                {scans.map((scan, idx) => {
                  const isSelected = compareScans.find(s => s.id === scan.id);
                  const colorIndex = compareScans.findIndex(s => s.id === scan.id);
                  
                  return (
                    <div
                      key={scan.id}
                      className={`p-3 rounded-sm border cursor-pointer transition-colors ${
                        compareMode && isSelected
                          ? "border-[#8B5CF6] bg-[#8B5CF6]/10"
                          : selectedScan?.id === scan.id && !compareMode
                            ? "border-[#34C759] bg-[#34C759]/5"
                            : "border-white/[0.08] hover:border-white/20 bg-[#0A0A0A]"
                      }`}
                      onClick={() => {
                        if (compareMode) {
                          toggleCompare(scan);
                        } else {
                          setSelectedScan(scan);
                        }
                      }}
                      data-testid={`scan-item-${scan.id}`}
                    >
                      <div className="flex items-start gap-2">
                        {compareMode && (
                          <div 
                            className={`mt-0.5 w-4 h-4 rounded flex items-center justify-center flex-shrink-0 ${
                              isSelected ? 'bg-[#8B5CF6]' : 'bg-white/10 border border-white/20'
                            }`}
                            style={isSelected ? { backgroundColor: compareColors[colorIndex] } : {}}
                          >
                            {isSelected && <Check className="w-3 h-3 text-white" />}
                          </div>
                        )}
                        <div className="flex-1 min-w-0">
                          <p className="font-medium text-sm text-[#F4F4F5] truncate">
                            {scan.name}
                          </p>
                          <p className="text-xs text-[#71717A]">
                            {new Date(scan.created_at).toLocaleDateString()} {new Date(scan.created_at).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})}
                          </p>
                          <p className="text-[10px] text-[#52525B] mt-0.5">
                            {scan.band_min}-{scan.band_max} MHz
                          </p>
                        </div>
                        {!compareMode && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDelete(scan.id);
                            }}
                            className="h-6 w-6 p-0 text-[#71717A] hover:text-[#FF3B30] flex-shrink-0"
                            data-testid={`delete-scan-${scan.id}`}
                          >
                            <Trash2 className="w-3 h-3" />
                          </Button>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Add Priority Zone Dialog */}
      <Dialog open={showZoneDialog} onOpenChange={setShowZoneDialog}>
        <DialogContent className="bg-[#111111] border-white/10 max-w-sm">
          <DialogHeader>
            <DialogTitle className="font-heading text-[#F4F4F5] flex items-center gap-2">
              <Radio className="w-5 h-5 text-[#8B5CF6]" />
              Nueva Zona de Prioridad
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 pt-4">
            <div>
              <Label className="text-[#A1A1AA] text-sm">Frecuencia Inicial (MHz)</Label>
              <Input
                type="number"
                value={newZone.startFreq}
                onChange={(e) => setNewZone(prev => ({ ...prev, startFreq: e.target.value }))}
                className="input-tactical mt-1"
                min={400}
                max={900}
                step={0.5}
                data-testid="zone-start-freq-input"
              />
              <p className="text-xs text-[#71717A] mt-1">
                Rango: {newZone.startFreq} - {parseFloat(newZone.startFreq) + ZONE_BANDWIDTH} MHz (6 MHz de ancho)
              </p>
            </div>
            
            <div>
              <Label className="text-[#A1A1AA] text-sm">Prioridad</Label>
              <div className="grid grid-cols-2 gap-2 mt-2">
                {Object.entries(priorityConfig).map(([key, config]) => (
                  <button
                    key={key}
                    type="button"
                    onClick={() => setNewZone(prev => ({ ...prev, priority: key }))}
                    className={`p-3 rounded-sm border transition-all flex items-center gap-2 ${
                      newZone.priority === key 
                        ? 'border-2' 
                        : 'border-white/[0.08] hover:border-white/20'
                    }`}
                    style={{ 
                      borderColor: newZone.priority === key ? config.color : undefined,
                      backgroundColor: newZone.priority === key ? `${config.color}15` : 'transparent'
                    }}
                    data-testid={`zone-priority-${key}`}
                  >
                    <div 
                      className="w-3 h-3 rounded-full"
                      style={{ backgroundColor: config.color }}
                    />
                    <span className={`text-sm ${newZone.priority === key ? 'text-[#F4F4F5]' : 'text-[#A1A1AA]'}`}>
                      {config.label}
                    </span>
                  </button>
                ))}
              </div>
            </div>
            
            <div className="flex justify-end gap-2 pt-4">
              <Button
                variant="ghost"
                onClick={() => setShowZoneDialog(false)}
                className="text-[#71717A]"
              >
                Cancelar
              </Button>
              <Button
                onClick={addPriorityZone}
                className="bg-[#8B5CF6] hover:bg-[#8B5CF6]/80 text-white"
                data-testid="confirm-add-zone-button"
              >
                <Plus className="w-4 h-4 mr-2" />
                Agregar Zona
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* New Scan Dialog */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="bg-[#111111] border-white/10 max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-heading text-[#F4F4F5]">
              Nuevo Escaneo RF
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="form-group">
              <Label className="form-label">Nombre del Escaneo</Label>
              <Input
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                className="input-tactical"
                placeholder="Ej: Pre-show Teatro Municipal"
                required
                data-testid="scan-name-input"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="form-group">
                <Label className="form-label">Banda Mín (MHz)</Label>
                <Input
                  type="number"
                  value={form.band_min}
                  onChange={(e) =>
                    setForm({ ...form, band_min: parseFloat(e.target.value) })
                  }
                  className="input-tactical font-mono"
                  data-testid="scan-band-min-input"
                />
              </div>
              <div className="form-group">
                <Label className="form-label">Banda Máx (MHz)</Label>
                <Input
                  type="number"
                  value={form.band_max}
                  onChange={(e) =>
                    setForm({ ...form, band_max: parseFloat(e.target.value) })
                  }
                  className="input-tactical font-mono"
                  data-testid="scan-band-max-input"
                />
              </div>
            </div>

            <div className="form-group">
              <div className="flex items-center justify-between mb-2">
                <Label className="form-label mb-0">
                  Datos del Escaneo (freq,level)
                </Label>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={generateSampleData}
                  className="text-xs text-[#34C759] hover:text-[#34C759]"
                  data-testid="generate-sample-data"
                >
                  <Upload className="w-3 h-3 mr-1" />
                  Generar Ejemplo
                </Button>
              </div>
              <Textarea
                value={form.scan_data_text}
                onChange={(e) =>
                  setForm({ ...form, scan_data_text: e.target.value })
                }
                className="input-tactical font-mono h-32 text-xs"
                placeholder="560,-85&#10;562,-82&#10;564,-78"
                required
                data-testid="scan-data-textarea"
              />
              <p className="text-xs text-[#71717A] mt-1">
                Una línea por punto: frecuencia,nivel (dBm)
              </p>
            </div>

            <div className="flex justify-end gap-3 pt-4">
              <Button
                type="button"
                variant="ghost"
                onClick={() => setShowDialog(false)}
                className="btn-tactical btn-tactical-secondary"
              >
                Cancelar
              </Button>
              <Button
                type="submit"
                className="btn-tactical btn-tactical-primary"
                data-testid="save-scan-button"
              >
                Guardar Escaneo
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Quick Save Dialog */}
      <Dialog open={showSaveDialog} onOpenChange={setShowSaveDialog}>
        <DialogContent className="bg-[#111111] border-white/10 max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading text-[#F4F4F5] flex items-center gap-2">
              <Save className="w-5 h-5 text-[#34C759]" />
              Guardar Escaneo Actual
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="form-group">
              <Label className="form-label">Nombre del Escaneo</Label>
              <Input
                value={quickSaveName}
                onChange={(e) => setQuickSaveName(e.target.value)}
                className="input-tactical"
                placeholder={`Escaneo ${new Date().toLocaleDateString()}`}
              />
            </div>
            
            <div className="p-3 bg-[#0A0A0A] border border-white/[0.05] rounded-sm">
              <p className="text-xs text-[#71717A]">Se guardará el espectro actual:</p>
              <p className="text-sm text-white font-mono mt-1">
                {zoomRange.min} - {zoomRange.max} MHz
              </p>
              <p className="text-xs text-[#52525B] mt-1">
                {liveSpectrum.length} puntos de datos
              </p>
            </div>

            <div className="flex justify-end gap-3">
              <Button
                variant="ghost"
                onClick={() => {
                  setShowSaveDialog(false);
                  setQuickSaveName("");
                }}
                className="text-[#A1A1AA] hover:text-white"
              >
                Cancelar
              </Button>
              <Button
                onClick={handleQuickSave}
                disabled={savingScan || !quickSaveName.trim()}
                className="bg-[#34C759] hover:bg-[#34C759]/90 text-white"
              >
                {savingScan ? (
                  <>
                    <span className="animate-spin mr-2">⏳</span>
                    Guardando...
                  </>
                ) : (
                  <>
                    <Save className="w-4 h-4 mr-2" />
                    Guardar
                  </>
                )}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Relacart Import Preview Dialog */}
      <Dialog open={showImportDialog} onOpenChange={(open) => {
        setShowImportDialog(open);
        if (!open) setImportedData(null);
      }}>
        <DialogContent className="bg-[#111111] border-white/10 max-w-2xl">
          <DialogHeader>
            <DialogTitle className="font-heading text-[#F4F4F5] flex items-center gap-2">
              <Radio className="w-5 h-5 text-[#34C759]" />
              Importar Escaneo Relacart
            </DialogTitle>
          </DialogHeader>
          
          {importedData && (
            <div className="space-y-4">
              {/* File Info */}
              <div className="p-4 bg-[#0A0A0A] border border-white/[0.05] rounded-sm">
                <div className="flex items-center justify-between mb-3">
                  <div>
                    <h3 className="font-medium text-[#F4F4F5]">{importedData.name}</h3>
                    {importedData.siteName && (
                      <p className="text-xs text-[#71717A] mt-1">{importedData.siteName}</p>
                    )}
                  </div>
                  <span className={`px-2 py-1 text-xs rounded ${
                    importedData.type === 'spectrum' 
                      ? 'bg-[#34C759]/20 text-[#34C759]' 
                      : 'bg-[#8B5CF6]/20 text-[#8B5CF6]'
                  }`}>
                    {importedData.type === 'spectrum' ? 'Escaneo RF' : 'Coordinación'}
                  </span>
                </div>
                
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <p className="text-[#71717A]">Rango de Frecuencia</p>
                    <p className="font-mono text-[#F4F4F5]">
                      {importedData.band_min} - {importedData.band_max} MHz
                    </p>
                  </div>
                  <div>
                    <p className="text-[#71717A]">
                      {importedData.type === 'spectrum' ? 'Puntos de Datos' : 'Sistemas'}
                    </p>
                    <p className="font-mono text-[#F4F4F5]">
                      {importedData.type === 'spectrum' ? importedData.total_points : importedData.total_systems}
                    </p>
                  </div>
                  {importedData.groups && importedData.groups.length > 0 && (
                    <div className="col-span-2">
                      <p className="text-[#71717A]">Grupos</p>
                      <div className="flex gap-2 mt-1">
                        {importedData.groups.map((g) => (
                          <span key={g} className="px-2 py-0.5 text-xs rounded bg-white/10 text-[#F4F4F5]">
                            {g}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Spectrum Preview Chart */}
              {importedData.type === 'spectrum' && importedData.scan_data?.length > 0 && (
                <div className="bg-[#0A0A0A] border border-white/[0.05] rounded-sm p-4">
                  <h4 className="text-sm font-medium text-[#F4F4F5] mb-3">Vista Previa del Espectro</h4>
                  <div className="h-48">
                    <ResponsiveContainer width="100%" height="100%">
                      <AreaChart
                        data={importedData.scan_data}
                        margin={{ top: 5, right: 5, left: -20, bottom: 0 }}
                      >
                        <defs>
                          <linearGradient id="importGradient" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="5%" stopColor="#34C759" stopOpacity={0.3} />
                            <stop offset="95%" stopColor="#34C759" stopOpacity={0} />
                          </linearGradient>
                        </defs>
                        <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" vertical={false} />
                        <XAxis 
                          dataKey="frequency" 
                          tick={{ fill: "#71717A", fontSize: 9 }}
                          tickFormatter={(v) => `${v.toFixed(0)}`}
                        />
                        <YAxis 
                          tick={{ fill: "#71717A", fontSize: 9 }}
                          domain={[-100, -20]}
                        />
                        <Tooltip 
                          content={({ active, payload, label }) => (
                            active && payload?.length ? (
                              <div className="bg-[#1A1A1A] border border-white/10 rounded-sm p-2 text-xs">
                                <p className="text-[#34C759]">{label?.toFixed(3)} MHz</p>
                                <p className="text-[#A1A1AA]">{payload[0].value} dBm</p>
                              </div>
                            ) : null
                          )}
                        />
                        <Area
                          type="monotone"
                          dataKey="level"
                          stroke="#34C759"
                          fill="url(#importGradient)"
                          strokeWidth={1}
                        />
                      </AreaChart>
                    </ResponsiveContainer>
                  </div>
                </div>
              )}

              {/* Detected Peaks */}
              {importedData.type === 'spectrum' && importedData.detected_peaks?.length > 0 && (
                <div className="p-4 bg-[#FF9500]/10 border border-[#FF9500]/30 rounded-sm">
                  <div className="flex items-center gap-2 mb-2">
                    <AlertTriangle className="w-4 h-4 text-[#FF9500]" />
                    <h4 className="text-sm font-medium text-[#FF9500]">
                      Interferencias Detectadas ({importedData.detected_peaks.length})
                    </h4>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {importedData.detected_peaks.slice(0, 8).map((peak, idx) => (
                      <span 
                        key={idx}
                        className={`px-2 py-1 text-xs font-mono rounded ${
                          peak.severity === 'high' 
                            ? 'bg-[#FF3B30]/20 text-[#FF3B30]'
                            : peak.severity === 'medium'
                              ? 'bg-[#FF9500]/20 text-[#FF9500]'
                              : 'bg-[#FFCC00]/20 text-[#FFCC00]'
                        }`}
                      >
                        {peak.frequency.toFixed(3)} MHz ({peak.level} dBm)
                      </span>
                    ))}
                    {importedData.detected_peaks.length > 8 && (
                      <span className="px-2 py-1 text-xs text-[#71717A]">
                        +{importedData.detected_peaks.length - 8} más
                      </span>
                    )}
                  </div>
                </div>
              )}

              {/* Coordination Systems List */}
              {importedData.type === 'coordination' && importedData.systems?.length > 0 && (
                <div className="p-4 bg-[#0A0A0A] border border-white/[0.05] rounded-sm max-h-48 overflow-y-auto">
                  <h4 className="text-sm font-medium text-[#F4F4F5] mb-3">Sistemas Detectados</h4>
                  <div className="space-y-2">
                    {importedData.systems.map((sys, idx) => (
                      <div key={idx} className="flex items-center justify-between text-xs p-2 bg-[#111111] rounded">
                        <div>
                          <span className="font-medium text-[#F4F4F5]">{sys.name}</span>
                          <span className="text-[#71717A] ml-2">({sys.group})</span>
                        </div>
                        <div className="flex items-center gap-4">
                          <span className="text-[#71717A]">{sys.manufacturer} {sys.model}</span>
                          <span className="font-mono text-[#34C759]">{sys.frequency} MHz</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Info Note */}
              <div className="flex items-start gap-2 p-3 bg-[#8B5CF6]/10 border border-[#8B5CF6]/30 rounded-sm">
                <Info className="w-4 h-4 text-[#8B5CF6] mt-0.5 flex-shrink-0" />
                <p className="text-xs text-[#8B5CF6]">
                  Los datos fueron importados desde un archivo CSV del Relacart U.DISCOVER. 
                  Puedes guardarlos en el historial para comparaciones futuras.
                </p>
              </div>

              {/* Actions */}
              <div className="flex justify-end gap-3 pt-2">
                <Button
                  variant="ghost"
                  onClick={() => {
                    setShowImportDialog(false);
                    setImportedData(null);
                  }}
                  className="text-[#A1A1AA] hover:text-white"
                >
                  Cancelar
                </Button>
                <Button
                  onClick={handleViewImportedScan}
                  className="btn-tactical btn-tactical-secondary"
                  data-testid="view-imported-scan"
                >
                  Ver sin Guardar
                </Button>
                <Button
                  onClick={handleSaveImportedScan}
                  disabled={savingScan}
                  className="bg-[#34C759] hover:bg-[#34C759]/90 text-white"
                  data-testid="save-imported-scan"
                >
                  {savingScan ? (
                    <>
                      <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                      Guardando...
                    </>
                  ) : (
                    <>
                      <Save className="w-4 h-4 mr-2" />
                      Guardar en Historial
                    </>
                  )}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </Layout>
  );
}
